/**
 * Simple Web Audio API alarm helper · v2.6.9 batch BI-3
 * ======================================================
 * Plays a 3-beep alarm sequence for Admin Dispatch "signal lost" events.
 * No external assets — generated via OscillatorNode so it works offline.
 *
 * Browsers require a user-gesture before audio can play. We lazy-init the
 * AudioContext on the first call AND on a single click anywhere in the page
 * so the alarm is armed by the time a signal-lost event fires.
 */

import { isAudioLeader } from "@/lib/audioLeader";

let _ctx = null;
let _muted = false;

// Initialize muted state from localStorage so the dispatcher's preference
// persists across page reloads.
try {
  if (typeof window !== "undefined") {
    _muted = localStorage.getItem("rtg_dispatch_alarm_muted") === "1";
  }
} catch (e) {
  // Private mode / SSR — log so dev tools surface it but don't crash.
  if (typeof console !== "undefined") console.warn("[dispatchAlarm] localStorage read failed:", e?.message || e);
}

function ensureContext() {
  if (_ctx) return _ctx;
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  try {
    _ctx = new Ctor();
  } catch (e) {
    // AudioContext can fail on iOS Safari before a user gesture; log so
    // the dispatcher knows audio is unavailable but don't crash the map.
    if (typeof console !== "undefined") console.warn("[dispatchAlarm] AudioContext init failed:", e?.message || e);
    return null;
  }
  return _ctx;
}

// One short beep at the given frequency + duration.
function beep(freq = 880, durSec = 0.18, gain = 0.18) {
  const ctx = ensureContext();
  // v2.7.65+E243 · Multi-window dedupe — non-leader admin tabs stay silent.
  if (!ctx || _muted || !isAudioLeader()) return;
  const t0 = ctx.currentTime;
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = "square";
  osc.frequency.setValueAtTime(freq, t0);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.01);
  g.gain.setValueAtTime(gain, t0 + durSec - 0.02);
  g.gain.linearRampToValueAtTime(0, t0 + durSec);
  osc.connect(g).connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + durSec + 0.02);
}

/** 3-beep alarm for signal-lost. */
export function playSignalLostAlarm() {
  if (_muted) return;
  const ctx = ensureContext();
  if (!ctx) return;
  // Resume context if it was auto-suspended (Chrome auto-suspends on tab
  // background, will resume on next user interaction).
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  // 880 → 660 → 880 Hz, ~600ms total
  beep(880, 0.18);
  setTimeout(() => beep(660, 0.18), 220);
  setTimeout(() => beep(880, 0.22), 440);
}

/** Single recovery chime — softer, ascending. */
export function playSignalRecoveredChime() {
  if (_muted) return;
  const ctx = ensureContext();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  beep(660, 0.12, 0.12);
  setTimeout(() => beep(880, 0.14, 0.12), 140);
}

// ============================================================================
// CRITICAL ALARM · v2.6.9 batch BI-4
// ============================================================================
// When a driver is silent >60 s the server emits `signal_critical`. The
// dispatcher gets a LOUDER, REPEATING klaxon-style alarm until either:
//   (a) the driver pings again (recovery chime), OR
//   (b) the dispatcher manually acknowledges via the "ACK" button on the map.
//
// Tracked per driver_id so multiple critical drivers all sound until each is
// individually acknowledged. Internally uses setInterval handles.
const _criticalLoops = new Map(); // driver_id -> interval handle

function loudKlaxonBurst() {
  const ctx = ensureContext();
  if (!ctx || _muted) return;
  // Klaxon-style alarm: 220 → 880 Hz quick sweep, doubled, longer + louder
  beep(220, 0.12, 0.32);
  setTimeout(() => beep(880, 0.12, 0.32), 130);
  setTimeout(() => beep(220, 0.14, 0.32), 260);
  setTimeout(() => beep(880, 0.16, 0.32), 410);
}

/** Start the repeating critical alarm for a driver. Idempotent. */
export function startCriticalAlarm(driverId) {
  if (_criticalLoops.has(driverId)) return;
  loudKlaxonBurst(); // immediate
  const id = setInterval(loudKlaxonBurst, 2500); // repeat every 2.5 s
  _criticalLoops.set(driverId, id);
}

/** Stop the repeating critical alarm for a specific driver (recovery or ACK). */
export function stopCriticalAlarm(driverId) {
  const handle = _criticalLoops.get(driverId);
  if (handle) {
    clearInterval(handle);
    _criticalLoops.delete(driverId);
  }
}

/** Stop ALL critical alarms — used by the dispatcher's master ACK button
 *  and by component unmount. */
export function stopAllCriticalAlarms() {
  for (const handle of _criticalLoops.values()) clearInterval(handle);
  _criticalLoops.clear();
}

export function activeCriticalCount() {
  return _criticalLoops.size;
}

export function setAlarmMuted(muted) {
  _muted = !!muted;
  try {
    if (typeof window !== "undefined") {
      localStorage.setItem("rtg_dispatch_alarm_muted", _muted ? "1" : "0");
    }
  } catch (e) {
    if (typeof console !== "undefined") console.warn("[dispatchAlarm] localStorage write failed:", e?.message || e);
  }
  // If we're muting, kill any active critical loops too so the dispatcher
  // doesn't get re-beeped on the next interval tick.
  if (_muted) stopAllCriticalAlarms();
}

export function isAlarmMuted() {
  return _muted;
}

/** Arm the audio context on first user click — no-op if already armed. */
if (typeof window !== "undefined") {
  const arm = () => {
    const ctx = ensureContext();
    if (ctx && ctx.state === "suspended") ctx.resume().catch(() => {});
    window.removeEventListener("click", arm);
    window.removeEventListener("keydown", arm);
  };
  window.addEventListener("click", arm, { once: true });
  window.addEventListener("keydown", arm, { once: true });
}
